import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react-router';

import { client } from '../../client';
import DashboardLayout from '../layout/DashboardLayout';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label } from '../../components/ui';
import './index.css';

const CreatePage = () => {
  const navigate = useNavigate();
  const { isLoaded, userId } = useAuth();
  const [user, setUser] = useState([]);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageId, setPageId] = useState('');
  const [values, setValues] = useState({ Name: '', PName: '', Message: '' });
  const [errors, setErrors] = useState({ Name: '', PName: '', Message: '' });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues({
      ...values,
      [name]: value,
    });

    validateInput(name, value);
  };

  const validateInput = (name, value) => {
    let newErrors = { ...errors };

    if (value === '') {
      newErrors[name] = 'This field is required.';
    } else {
      newErrors[name] = '';
    }

    setErrors(newErrors);
  };

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    if (!userId) {
      navigate('/');
      return;
    }
    client.fetch(`*[_type == "user" && _id == "${userId}"]`).then((data) => {
      setUser(data);
    });
  }, [isLoaded, navigate, userId]);

  useEffect(() => {
    if (user[0]?.name) {
      setValues((prev) => ({ ...prev, Name: user[0].name }));
    }
  }, [user]);

  const handleSubmit = () => {
    setLoading(true);

    const page = {
      _type: 'page',
      partnerName: values.PName,
      message: values.Message,
      status: false,
      user: { _ref: userId },
    };

    client
      .create(page)
      .then((data) => {
        setPageId(data._id);
        setLoading(false);
        setIsFormSubmitted(true);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });

    if (!user[0]?.name || !user[0]?.phoneNumber) {
      client
        .patch(userId)
        .set({
          name: values.Name,
        })
        .commit();
    }
  };

  const handleVisite = () => {
    navigate(`/page/${pageId}`);
  };

  return (
    <DashboardLayout title="Create Proposal Page" subtitle="Set up a new proposal page and share it with your partner.">
      {!isFormSubmitted ? (
        <Card className="create-page-card">
          <CardHeader>
            <CardTitle>Page Details</CardTitle>
            <CardDescription>All fields are required to publish your proposal page.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="create-form-grid">
              <div className="form-item">
                <Label>Your Name</Label>
                {!user[0]?.name ? (
                  <>
                    <Input type="text" name="Name" value={values.Name} onChange={handleChange} />
                    <p className="error">{errors.Name}</p>
                  </>
                ) : (
                  <>
                    <p className="locked-value">{user[0].name}</p>
                    <p className="name-warning">Your name is saved and reused on new pages.</p>
                  </>
                )}
              </div>
              <div className="form-item">
                <Label>Your Partner's Name</Label>
                <Input type="text" name="PName" value={values.PName} onChange={handleChange} />
                <p className="error">{errors.PName}</p>
              </div>
              <div className="form-item">
                <Label>Your Message</Label>
                <Input type="text" name="Message" value={values.Message} onChange={handleChange} />
                <p className="error">{errors.Message}</p>
              </div>
            </div>
            <div className="form-btn">
              {values.Name && values.PName && values.Message ? (
                <Button onClick={handleSubmit} disabled={loading}>
                  {!loading ? 'Create Your Page' : 'Creating...'}
                </Button>
              ) : (
                <p className="ui-muted">Complete all fields before submitting.</p>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="create-page-card">
          <CardContent>
            <div className="page-link">
              <h2>Your page is ready.</h2>
              <p className="ui-muted">You can open it now and copy the link from dashboard list anytime.</p>
              <Button onClick={handleVisite}>Visit Page</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default CreatePage;
